/**
 * CompactCardHeader - A minimal card header for user profile views.
 * Shows a small avatar, quoted thought text, and a reaction button.
 * Based on Figma node 16968:55571.
 */

import React, { FC, useRef, useState } from 'react';
import { styled } from 'linaria/react';

import useTheme from 'helpers/useTheme';
import { FONTS } from '../../../styles/constants';
import { ReactComponent as AddReaction } from '../../../assets/icons/AddReaction.svg';
import ReactionMenu from '../../Reactions/ReactionMenu/ReactionMenu';
import { CompactCardHeaderProps } from '../types';
import Avatar from './Avatar';
import { isColorDark, getTextSecondary } from './colorUtils';

const COMPACT_AVATAR_SIZE = 20;

const StyledCompactHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 0 36px 0 12px;
  position: relative;
  width: 100%;
`;

const StyledFacepile = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  padding-right: 8px;
`;

const StyledThought = styled.div`
  flex: 1 0 0;
  min-height: 1px;
  min-width: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${FONTS.secondary};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--card-text-primary, #181800);
  display: flex;
  flex-direction: column;
  justify-content: center;
  white-space: nowrap;
`;

const StyledTimestamp = styled.div`
  flex: 1 0 0;
  min-height: 1px;
  min-width: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${FONTS.secondary};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--card-text-secondary, #7a7359);
  display: flex;
  flex-direction: column;
  justify-content: center;
  white-space: nowrap;
`;

const StyledReactionWrapper = styled.div`
  position: absolute;
  right: 0;
  top: -8px;
  z-index: 100;
  pointer-events: auto;
`;

const StyledReactionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 6px 8px;
  border-radius: 20px;
  backdrop-filter: blur(2px);
  background: none;
  border: none;
  cursor: pointer;
  margin: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const CompactCardHeader: FC<CompactCardHeaderProps> = ({
  avatars,
  thought,
  timestamp,
  backgroundColor,
  onOpenReactionsModal,
  className,
  onThoughtClick,
}) => {
  const primaryAvatar = avatars[0];

  const isDarkTheme = useTheme();
  // Contrast icon color against card background, falling back to theme
  const darkBg = backgroundColor ? isColorDark(backgroundColor) : isDarkTheme;
  const iconColor = getTextSecondary(darkBg);

  // Reaction menu state
  const addReactionButtonRef = useRef<HTMLButtonElement>(null);
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false);
  const [reactionMenuCoords, setReactionMenuCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const openReactionMenu = () => {
    if (addReactionButtonRef.current) {
      const rect = addReactionButtonRef.current.getBoundingClientRect();
      setReactionMenuCoords({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
    setIsReactionMenuOpen(true);
  };

  const closeReactionMenu = () => setIsReactionMenuOpen(false);

  const quotedThought = thought ? `\u201C${thought}\u201D` : null;

  return (
    <StyledCompactHeader className={className}>
      {primaryAvatar && (
        <StyledFacepile>
          <Avatar
            src={primaryAvatar.avatarUrl}
            fallbackLetter={primaryAvatar.username?.charAt(0) ?? primaryAvatar.name?.charAt(0) ?? '?'}
            size={COMPACT_AVATAR_SIZE}
          />
        </StyledFacepile>
      )}

      <div
        style={{
          flex: '1 0 0',
          minHeight: 1,
          minWidth: 1,
          overflow: 'hidden',
          ...(onThoughtClick ? { cursor: 'pointer' } : {}),
        }}
        onClick={
          onThoughtClick
            ? (e: React.MouseEvent) => {
                e.stopPropagation();
                onThoughtClick();
              }
            : undefined
        }
      >
        {quotedThought ? (
          <StyledThought>{quotedThought}</StyledThought>
        ) : (
          timestamp && <StyledTimestamp>{timestamp}</StyledTimestamp>
        )}
      </div>

      <StyledReactionWrapper onClick={(e) => e.stopPropagation()}>
        <StyledReactionButton
          ref={addReactionButtonRef}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            openReactionMenu();
          }}
          type="button"
          aria-label="Add reaction"
          style={{ color: iconColor }}
        >
          <AddReaction />
        </StyledReactionButton>
        <ReactionMenu
          isOpen={isReactionMenuOpen}
          anchorPoint={reactionMenuCoords}
          onClose={closeReactionMenu}
          onSend={() => {}}
          onEmoji={() => {}}
          onOpenReactionsModal={onOpenReactionsModal}
        />
      </StyledReactionWrapper>
    </StyledCompactHeader>
  );
};

export default CompactCardHeader;
